import { ENV } from "../../../../utils";

export function getProductResponse(result) {
  return result?.product || result?.response || result || null;
}

export function getImageUrl(imagePath) {
  if (!imagePath) {
    return "";
  }

  const value = String(imagePath);

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  return `${ENV.SERVER_HOST}/${value.replace(/\\/g, "/").replace(/^\/+/, "")}`;
}

export function getVariantAttributeName(attribute) {
  if (attribute?.schema && typeof attribute.schema === "object") {
    return attribute.schema.name || attribute.name || "";
  }

  return attribute?.name || "";
}

export function getVariantEffectivePrice(variant) {
  const offerPrice = Number(variant?.offerPrice);

  if (Number.isFinite(offerPrice) && offerPrice > 0) {
    return offerPrice;
  }

  return Number(variant?.price) || 0;
}

/*
 * Agrupa los atributos de todas las variantes habilitadas por nombre
 * (p. ej. "Talla" → ["S","M","L"], "Color" → ["Negro","Café"]), en el
 * orden en que aparecen, para poder armar los selectores del producto.
 *
 * Cada valor se identifica por su nombre legible (attribute.label), no
 * por el valor crudo: para "Color" ese valor crudo es el hexadecimal, que
 * no sirve para identificar la variante ni para mostrarlo al cliente. El
 * hexadecimal (value) se conserva solo para pintar el swatch. Para
 * atributos como "Talla", label es igual a value, así que no cambia nada.
 */
export function buildAttributeGroups(variants) {
  const groups = new Map();

  (variants || []).forEach((variant) => {
    (variant.attributes || []).forEach((attribute) => {
      const name = getVariantAttributeName(attribute);

      if (!name || !attribute.value) {
        return;
      }

      const label = attribute.label || attribute.value;

      if (!groups.has(name)) {
        groups.set(name, new Map());
      }

      const values = groups.get(name);

      if (!values.has(label)) {
        values.set(label, attribute.value);
      }
    });
  });

  return [...groups.entries()].map(([name, values]) => ({
    name,
    values: [...values.entries()].map(([label, value]) => ({ label, value })),
  }));
}

export function getVariantAttributeMap(variant) {
  const map = {};

  (variant.attributes || []).forEach((attribute) => {
    const name = getVariantAttributeName(attribute);

    if (name) {
      map[name] = attribute.label || attribute.value;
    }
  });

  return map;
}

/*
 * Busca la variante habilitada cuyos atributos coinciden exactamente
 * con la selección actual. Si nada coincide (selección incompleta),
 * regresa null.
 */
export function findVariantBySelection(variants, selection) {
  return (
    (variants || []).find((variant) => {
      const attributeMap = getVariantAttributeMap(variant);

      return Object.entries(selection).every(
        ([name, value]) => !(name in attributeMap) || attributeMap[name] === value
      );
    }) || null
  );
}

export function getImageIndex(images, activeImage) {
  const index = (images || []).indexOf(activeImage);

  return index === -1 ? 0 : index;
}

export function getCyclicImage(images, activeImage, delta) {
  const list = images || [];

  if (list.length === 0) {
    return "";
  }

  const currentIndex = getImageIndex(list, activeImage);
  const nextIndex = (currentIndex + delta + list.length) % list.length;

  return list[nextIndex];
}

export function getVariantImages(product, variant) {
  const variantImages = Array.isArray(variant?.images) ? variant.images : [];

  if (variantImages.length > 0) {
    return variantImages;
  }

  const productImages = Array.isArray(product?.images) ? product.images : [];

  return [...productImages]
    .sort((first, second) => {
      if (first.isCover === second.isCover) {
        return (first.order || 0) - (second.order || 0);
      }

      return first.isCover ? -1 : 1;
    })
    .map((image) => image.path);
}

export function groupCharacteristicsBySchema(characteristics) {
  const list = Array.isArray(characteristics) ? characteristics : [];

  const groupsBySchemaId = new Map();

  list.forEach((characteristic) => {
    const schema = characteristic?.schema;
    const schemaId = schema?._id || schema?.id || "sin-esquema";
    const schemaName =
      (typeof schema === "object" && schema?.name) || "Características";

    if (!groupsBySchemaId.has(schemaId)) {
      groupsBySchemaId.set(schemaId, {
        schemaName: schemaName || "Características",
        schemaOrder: Number(schema?.order) || 0,
        items: [],
      });
    }

    groupsBySchemaId.get(schemaId).items.push(characteristic);
  });

  return [...groupsBySchemaId.values()]
    .map((group) => ({
      ...group,
      items: [...group.items].sort(
        (first, second) => (Number(first.order) || 0) - (Number(second.order) || 0)
      ),
    }))
    .sort((first, second) => first.schemaOrder - second.schemaOrder);
}

export function buildAttributesLabel(selection) {
  return Object.entries(selection)
    .map(([name, value]) => `${name}: ${value}`)
    .join(" · ");
}

export function isColorAttributeGroup(name) {
  return String(name || "").trim().toLowerCase() === "color";
}
